import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import useLatest from 'react-use/esm/useLatest';
import { reporter } from 'modules/analytics/constants';
import { performance } from 'utils/performance';
import type { useReporterLoadState } from './ReporterLoadTelemetry';

export const reporterActionMeasures = {
  readyToRecord: reporter.sys.caseOpenTimeToReadyToRecord,
  examsLinkable: reporter.sys.caseOpenTimeToExamsLinkable,
  templatesInsertable: reporter.sys.caseOpenTimeToTemplatesInsertable,
} as const;

export type ReporterAction = keyof typeof reporterActionMeasures;
type LoadSnapshot = ReturnType<typeof useReporterLoadState>['snapshot'];
type ActionDimensions = Readonly<Record<string, string | number | boolean>>;

// Actions that need the report editable with any autoload template applied.
const actionsRequiringReport: ReadonlySet<ReporterAction> = new Set([
  'readyToRecord',
  'templatesInsertable',
]);

interface AutoloadApplication {
  visitId: string;
  isApplied: boolean;
}

interface ReporterActionReadiness {
  reportReady: boolean;
  complete: (action: ReporterAction, worklistItemId: string, dimensions?: ActionDimensions) => void;
  setAutoloadApplied: (params: { worklistItemId: Nullable<string>; isApplied: boolean }) => void;
}

const ReporterActionReadinessContext = createContext<ReporterActionReadiness | null>(null);

function isEditable(state: LoadSnapshot['state']): boolean {
  return state.status === 'ready' && state.readyState === 'editable';
}

// Times each action from case open, at most once per visit. The case lifecycle and its start
// belong to the load telemetry snapshot.
export const ReporterActionReadinessProvider = ({
  snapshot,
  children,
}: {
  snapshot: LoadSnapshot;
  children: ReactNode;
}) => {
  const { visitId, caseId, observedAt, loadType } = snapshot;
  const latestSnapshot = useLatest(snapshot);
  // State re-renders consumers. The ref lets a completion queued in the same commit see a
  // publication that revoked readiness before React re-rendered.
  const [autoload, setAutoload] = useState<AutoloadApplication | null>(null);
  const autoloadRef = useRef<AutoloadApplication | null>(null);
  const completedRef = useRef({ visitId, actions: new Set<ReporterAction>() });

  const isCurrentVisit = useCallback(
    (worklistItemId: Nullable<string>) =>
      visitId !== '' && latestSnapshot.current.visitId === visitId && worklistItemId === caseId,
    [visitId, caseId, latestSnapshot]
  );

  const setAutoloadApplied = useCallback(
    ({ worklistItemId, isApplied }: { worklistItemId: Nullable<string>; isApplied: boolean }) => {
      if (!isCurrentVisit(worklistItemId)) return;
      const application = { visitId, isApplied };
      autoloadRef.current = application;
      setAutoload(application);
    },
    [isCurrentVisit, visitId]
  );

  const complete = useCallback(
    (action: ReporterAction, worklistItemId: string, dimensions?: ActionDimensions) => {
      if (!isCurrentVisit(worklistItemId) || observedAt == null || loadType == null) return;
      if (completedRef.current.visitId !== visitId) {
        completedRef.current = { visitId, actions: new Set() };
      }
      const { actions } = completedRef.current;
      if (actions.has(action)) return;
      if (actionsRequiringReport.has(action)) {
        const application = autoloadRef.current;
        if (
          application?.visitId !== visitId ||
          !application.isApplied ||
          !isEditable(latestSnapshot.current.state)
        )
          return;
      }
      actions.add(action);
      performance.measure(reporterActionMeasures[action], {
        start: observedAt,
        end: performance.now(),
        detail: { ...dimensions, worklistItemId, load_type: loadType },
      });
    },
    [isCurrentVisit, visitId, observedAt, loadType, latestSnapshot]
  );

  const reportReady =
    autoload?.visitId === visitId && autoload.isApplied && isEditable(snapshot.state);
  const value = useMemo(
    () => ({ reportReady, complete, setAutoloadApplied }),
    [reportReady, complete, setAutoloadApplied]
  );
  return (
    <ReporterActionReadinessContext.Provider value={value}>
      {children}
    </ReporterActionReadinessContext.Provider>
  );
};

// Other editors and isolated stories have no Reporter owner and intentionally emit nothing.
export const useReporterActionReadiness = () => useContext(ReporterActionReadinessContext);

export const useReporterActionCompletion = ({
  action,
  worklistItemId,
  ready,
  dimensions,
}: {
  action: ReporterAction;
  worklistItemId: Nullable<string>;
  ready: boolean;
  dimensions?: ActionDimensions;
}) => {
  const complete = useReporterActionReadiness()?.complete;
  const latestDimensions = useLatest(dimensions);
  useEffect(() => {
    if (ready && worklistItemId != null)
      complete?.(action, worklistItemId, latestDimensions.current);
  }, [action, complete, ready, worklistItemId, latestDimensions]);
};
